import React, { useEffect, useState } from "react";
import { Link, Navigate, useLocation } from "react-router-dom";
import * as Icons from "lucide-react";
import api, { fmtDateTime } from "@/lib/api";
import { PageHeader } from "@/components/common";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { PriorityBadge, StatusBadge, SlaChip, useSd } from "@/components/servicedesk/SdBits";

const SCOPES = {
  "/servicedesk/my": { scope: "mine", title: "My Tickets", sub: "Tickets you submitted" },
  "/servicedesk/approvals": { scope: "approvals", title: "Approvals", sub: "Service requests waiting for your approval" },
  "/servicedesk/queue": { scope: "queue", title: "Ticket Queue", sub: "All tickets you support" },
};
const QUEUE_TABS = [["open", "Open"], ["unassigned", "Unassigned"], ["assigned", "Assigned to me"], ["team", "My teams"], ["escalated", "Escalated"], ["breached", "SLA breached"],
  ["pending", "Pending"], ["major", "Major incidents"], ["left", "Requester left"], ["", "All"]];

const Row = ({ t }) => (
  <Link to={`/servicedesk/tickets/${t.id}`} className="grid grid-cols-[auto_1fr] sm:grid-cols-[110px_1fr_140px_150px] gap-x-3 gap-y-1 items-center px-4 py-3 border-t border-slate-100 hover:bg-slate-50 transition-colors" data-testid={`sd-row-${t.number}`}>
    <div className="font-mono text-xs text-slate-500">{t.number}</div>
    <div className="min-w-0">
      <div className="flex items-center gap-2"><PriorityBadge p={t.priority} />{t.is_major && <span className="text-[10px] font-bold text-red-600">MAJOR</span>}{t.requester_left && <span className="text-[10px] font-bold text-rose-700" data-testid="sd-left-flag">REQUESTER LEFT</span>}<span className="text-sm font-medium text-slate-800 truncate">{t.subject}</span></div>
      <div className="text-xs text-slate-500 truncate">{t.type} · {t.category}{t.subcategory ? ` / ${t.subcategory}` : ""} · {t.requester_name} · {t.company_name}</div>
    </div>
    <div className="col-start-2 sm:col-start-auto flex flex-col gap-0.5"><StatusBadge s={t.status} /><SlaChip t={t} /></div>
    <div className="col-start-2 sm:col-start-auto text-xs text-slate-500">{t.assignee_name || <span className="text-amber-600">Unassigned</span>}{t.team_name && <div className="text-[11px] text-slate-500">{t.team_name}</div>}<div className="text-[11px] text-slate-400">{fmtDateTime(t.created_at)}</div></div>
  </Link>
);

export default function SdTickets() {
  const { pathname } = useLocation();
  const cfg = SCOPES[pathname] || SCOPES["/servicedesk/my"];
  const { cat, me } = useSd();
  const [tab, setTab] = useState(cfg.scope === "queue" ? "open" : "");
  const [q, setQ] = useState("");
  const [prio, setPrio] = useState("all");
  const [page, setPage] = useState(1);
  const [data, setData] = useState(null);

  useEffect(() => { setTab(cfg.scope === "queue" ? "open" : ""); setPage(1); }, [cfg.scope]);
  useEffect(() => {
    const scope = tab === "assigned" ? "assigned" : cfg.scope;
    const status = tab === "assigned" ? "open" : tab;
    const t = setTimeout(() => {
      api.get("/servicedesk/tickets", { params: { scope, status, q, priority: prio === "all" ? "" : prio, page } }).then(({ data }) => setData(data)).catch(() => setData({ rows: [], total: 0, pages: 1 }));
    }, 250);
    return () => clearTimeout(t);
  }, [cfg.scope, tab, q, prio, page]);

  if (cfg.scope === "queue" && me && !me.is_staff) return <Navigate to="/servicedesk/my" replace />;
  return (
    <div data-testid="sd-tickets-page">
      <PageHeader title={cfg.title} subtitle={cfg.sub} actions={<Button asChild data-testid="sd-new-btn"><Link to="/servicedesk/new"><Icons.Plus className="h-4 w-4 mr-1" />Submit a Ticket</Link></Button>} />
      {cfg.scope === "queue" && (
        <div className="flex gap-1.5 overflow-x-auto pb-2 mb-3" data-testid="sd-queue-tabs">
          {QUEUE_TABS.map(([k, l]) => (
            <button key={k || "all"} onClick={() => { setTab(k); setPage(1); }} className={`px-3 py-1.5 rounded-full text-xs whitespace-nowrap border transition-colors ${tab === k ? "bg-slate-900 text-white border-slate-900" : "bg-white text-slate-600 border-slate-200 hover:border-slate-400"}`} data-testid={`sd-tab-${k || "all"}`}>{l}</button>
          ))}
        </div>
      )}
      <div className="flex flex-col sm:flex-row gap-2 mb-3">
        <div className="relative flex-1"><Icons.Search className="h-4 w-4 absolute left-3 top-2.5 text-slate-400" /><Input value={q} onChange={(e) => { setQ(e.target.value); setPage(1); }} placeholder="Search ticket no., subject, requester or technician" className="pl-9" data-testid="sd-search" /></div>
        <Select value={prio} onValueChange={(v) => { setPrio(v); setPage(1); }}>
          <SelectTrigger className="sm:w-44" data-testid="sd-priority-filter"><SelectValue /></SelectTrigger>
          <SelectContent><SelectItem value="all">All priorities</SelectItem>{Object.entries(cat?.priorities || {}).map(([k, l]) => <SelectItem key={k} value={k}>{l}</SelectItem>)}</SelectContent>
        </Select>
      </div>
      <div className="bg-white rounded-xl border border-slate-200 overflow-hidden" data-testid="sd-ticket-list">
        {!data ? <div className="p-8 text-center text-sm text-slate-500">Loading…</div>
          : !data.rows.length ? <div className="p-10 text-center text-sm text-slate-500" data-testid="sd-empty">No tickets here.</div>
            : data.rows.map((t) => <Row key={t.id} t={t} />)}
      </div>
      {data && data.pages > 1 && (
        <div className="flex items-center justify-end gap-2 mt-3 text-xs text-slate-500" data-testid="sd-pager">
          {data.total} tickets · page {page} of {data.pages}
          <Button size="sm" variant="outline" disabled={page <= 1} onClick={() => setPage(page - 1)} data-testid="sd-prev">Previous</Button>
          <Button size="sm" variant="outline" disabled={page >= data.pages} onClick={() => setPage(page + 1)} data-testid="sd-next">Next</Button>
        </div>
      )}
    </div>
  );
}
